import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class SensorService {
  constructor() {
    this.apiClient = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor for logging
    this.apiClient.interceptors.request.use(
      (config) => {
        console.log(`Making API request to: ${config.url}`);
        return config;
      },
      (error) => {
        console.error('API request error:', error);
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.apiClient.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        console.error('API response error:', error);
        if (error.response) {
          // Server responded with error status
          throw new Error(`API Error: ${error.response.data?.message || error.response.statusText}`);
        } else if (error.request) {
          // Request was made but no response received
          throw new Error('Network Error: Unable to connect to server');
        } else {
          // Something else happened
          throw new Error(`Request Error: ${error.message}`);
        }
      }
    );
  }

  /**
   * Get latest sensor data
   * @returns {Promise<Object>} Latest sensor data
   */
  async getLatestSensorData() {
    try {
      const response = await this.apiClient.get('/sensors/latest');
      return response.data;
    } catch (error) {
      console.error('Error fetching latest sensor data:', error);
      throw error;
    }
  }

  /**
   * Get sensor health status
   * @returns {Promise<Object>} Sensor health status
   */
  async getSensorHealth() {
    try {
      const response = await this.apiClient.get('/sensors/health');
      return response.data;
    } catch (error) {
      console.error('Error fetching sensor health:', error);
      throw error;
    }
  }

  /**
   * Get comprehensive dashboard data
   * @returns {Promise<Object>} Dashboard data with analytics
   */
  async getDashboardData() {
    try {
      const response = await this.apiClient.get('/sensors/dashboard');
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  }

  /**
   * Get sensor API information
   * @returns {Promise<Object>} API information
   */
  async getSensorApiInfo() {
    try {
      const response = await this.apiClient.get('/sensors');
      return response.data;
    } catch (error) {
      console.error('Error fetching sensor API info:', error);
      throw error;
    }
  }

  /**
   * Get sensor data for the past day
   * @returns {Promise<Object>} Past day sensor data
   */
  async getPastDaySensorData() {
    try {
      const response = await this.apiClient.get('/sensors/past-day');
      return response.data;
    } catch (error) {
      console.error('Error fetching past day sensor data:', error);
      throw error;
    }
  }
  
  /**
   * Get sensor data for a specific date range
   * @param {Date} fromDate - Start date
   * @param {Date} toDate - End date
   * @returns {Promise<Object>} Date range sensor data
   */
  async getDateRangeSensorData(fromDate, toDate) {
    try {
      // Format dates as ISO strings for the API
      const fromDateStr = fromDate.toISOString();
      const toDateStr = toDate.toISOString();
      
      // Make a request to a dedicated endpoint for date range queries
      // This endpoint should query MongoDB for the specific date range
      const response = await this.apiClient.get('/sensors/date-range', {
        params: {
          fromDate: fromDateStr,
          toDate: toDateStr
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching date range sensor data:', error);
      
      // Fallback to past-day data if the date-range endpoint fails or doesn't exist
      try {
        console.log('Falling back to past-day data and filtering client-side');
        const response = await this.apiClient.get('/sensors/past-day');
        
        // If the API call was successful but we need to filter the data
        if (response.data && response.data.success) {
          // Clone the response to avoid mutating the original
          const filteredResponse = { ...response.data };
          
          // Filter the data based on the date range
          if (filteredResponse.data && Array.isArray(filteredResponse.data)) {
            filteredResponse.data = filteredResponse.data.filter(item => {
              const itemDate = new Date(item.timestamp);
              return itemDate >= fromDate && itemDate <= toDate;
            });
          }
          
          return filteredResponse;
        }
        
        return response.data;
      } catch (fallbackError) {
        console.error('Error in fallback method:', fallbackError);
        throw error; // Throw the original error
      }
    }
  }
}

export default new SensorService();
